import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Phone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import Realisations from "@/pages/Realisations";
import APropos from "@/pages/APropos";
import Devis from "@/pages/Devis";
import Contact from "@/pages/Contact";
import ZoneIntervention from "@/pages/ZoneIntervention";
import Blog from "@/pages/Blog";
import LocalPage from "@/pages/LocalPage";
import ChantierPage from "@/pages/ChantierPage";
import ArticlePage from "@/pages/ArticlePage";
import { MentionsLegales, PolitiqueConfidentialite, CookiesPage } from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import Admin from "@/pages/Admin";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App min-h-screen bg-[#0A0A0A]">
        <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/resine-decorative" element={<ServicePage slug="resine-decorative" />} />
              <Route path="/resine-industrielle" element={<ServicePage slug="resine-industrielle" />} />
              <Route path="/resine-pailletee" element={<ServicePage slug="resine-pailletee" />} />
              <Route path="/beton-cire" element={<ServicePage slug="beton-cire" />} />
              <Route path="/moquette-de-pierre" element={<ServicePage slug="moquette-de-pierre" />} />
              <Route path="/peinture-batiment" element={<ServicePage slug="peinture-batiment" />} />
              <Route path="/realisations" element={<Realisations />} />
              <Route path="/zone-intervention" element={<ZoneIntervention />} />
              <Route path="/a-propos" element={<APropos />} />
              <Route path="/devis" element={<Devis />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/resine-sol-toulouse" element={<LocalPage slug="resine-sol-toulouse" />} />
              <Route path="/resine-sol-daux" element={<LocalPage slug="resine-sol-daux" />} />
              <Route path="/resine-sol-bordeaux" element={<LocalPage slug="resine-sol-bordeaux" />} />
              <Route path="/resine-sol-montpellier" element={<LocalPage slug="resine-sol-montpellier" />} />
              <Route path="/resine-sol-lyon" element={<LocalPage slug="resine-sol-lyon" />} />
              <Route path="/resine-sol-nantes" element={<LocalPage slug="resine-sol-nantes" />} />
              <Route path="/resine-sol-lille" element={<LocalPage slug="resine-sol-lille" />} />
              <Route path="/resine-sol-nice" element={<LocalPage slug="resine-sol-nice" />} />
              <Route path="/resine-sol-montauban" element={<LocalPage slug="resine-sol-montauban" />} />
              <Route path="/resine-sol-blagnac" element={<LocalPage slug="resine-sol-blagnac" />} />
              <Route path="/resine-sol-colomiers" element={<LocalPage slug="resine-sol-colomiers" />} />
              <Route path="/resine-sol-tournefeuille" element={<LocalPage slug="resine-sol-tournefeuille" />} />
              <Route path="/resine-sol-muret" element={<LocalPage slug="resine-sol-muret" />} />
              <Route path="/peinture-toulouse" element={<LocalPage slug="peinture-toulouse" />} />
              <Route path="/realisations/:slug" element={<ChantierPage />} />
              <Route path="/blog/:slug" element={<ArticlePage />} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/admin" element={<Admin />} />
              {/* Redirections des anciennes pages du site précédent */}
              <Route path="/resine-tertiaire" element={<Navigate to="/resine-industrielle" replace />} />
              <Route path="/peinture-toiture" element={<Navigate to="/peinture-batiment" replace />} />
              <Route path="/peinture-cool-roof" element={<Navigate to="/peinture-batiment" replace />} />
              <Route path="/formation-resine-industrielle" element={<Navigate to="/resine-industrielle" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/confidentialite" replace />} />
              <Route path="/conditions-generales-d-utilisation" element={<Navigate to="/mentions-legales" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="bottom-right" theme="dark" />
          <a
            href="tel:+33783693101"
            data-testid="floating-call-button"
            aria-label="Appeler Resineko"
            className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#047857] text-white shadow-lg shadow-[#047857]/40 transition-transform duration-300 hover:scale-110 lg:hidden"
          >
            <Phone size={22} />
          </a>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
